import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestApiService } from '@core/services/rest-api.service';
import FilterAreaUtils from '@core/utils/filter-area.utils';
import {
  DvDynamicFormUtils,
  DvFilter,
  DvFilterRangeKeys,
  DvFormUtils,
  DvPeriodPickerControlValue,
} from '@devinco/ui/legacy';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { DataFilterAction } from '@shared/constants/data-filter-action';
import { Entity } from '@shared/constants/entity';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { TranslateService } from '@ngx-translate/core';
import { WeekdaysForm } from '@pages/reports-page/components/report-tabs/report-information-tab/interfaces';
import { TripFilterDto } from '@pages/reports-page/interfaces';
import {
  excludedRequestValues,
  FilterInterval,
  PeriodInterval,
} from '@shared/components/edit-trip-filter-dialog/constants';
import { EditTripFilterDialogComponent } from '@shared/components/edit-trip-filter-dialog/edit-trip-filter-dialog.component';
import {
  DataFilterCreateEditForm,
  EditFilterDialogData,
} from '@shared/components/edit-trip-filter-dialog/interfaces';
import { DataFilterElementsService } from '@shared/components/edit-trip-filter-dialog/services/data-filter-elements.service';
import { ExtraFilterDataSaveService } from '@shared/components/edit-trip-filter-dialog/services/filter-data-save.service';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TripsGridComponent } from './components/trips-grid/trips-grid.component';
import { TripsFilter } from './interfaces';
import { TripFilterCrudDialogService } from './services/trip-filter-crud-dialog.service';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { DialogService } from '@core/services/dialog.service';
import { DvObjectUtils } from '@devinco/ui';
import TripDataFilterFormUtils from '@shared/components/edit-trip-filter-dialog/utils/trip-data-filter-form.utils';
import { VtFilterMenuItem } from '@shared/interfaces/vt-filter-menu-item';
import { VtFilterMenuItemType } from '@shared/constants/vt-filter-menu-item-type';
import CustomGridFilterUtils from '@core/utils/custom-grid-filter.utils';
import { DvSplitterResizableSide } from '@devinco/ui/dds/splitter';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

@Component({
  selector: 'app-trips-page-page',
  templateUrl: './trips-page.component.html',
  styleUrls: ['../../styles/page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private api = inject(RestApiService);
  private translate = inject(TranslateService);
  private dataFilterElementsService = inject(DataFilterElementsService);
  private filterDataSaveService = inject(ExtraFilterDataSaveService);
  private tripDataFilterDialogService = inject(TripFilterCrudDialogService);
  private snackBar = inject(DvSnackbarService);
  private dialog = inject(DialogService);

  resizableSide: typeof DvSplitterResizableSide = DvSplitterResizableSide;
  availableFilters$ = new BehaviorSubject<TripsFilter[]>([]);
  isTripFilterAreaOpen = true;
  selectedFilterName: string | undefined;
  private activeFilterId: number;
  private destroyRef = inject(DestroyRef);

  @ViewChild('tripsGrid')
  tripsGridComponent: TripsGridComponent;

  ngOnInit(): void {
    this.fetchDataFilters().subscribe();
    this.toggleFilterAreaVisibility();
  }

  ngAfterViewInit(): void {
    this.tripsGridComponent.formGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        debounceTime(500),
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        filter(() => this.tripsGridComponent?.formGroup?.valid)
      )
      .subscribe(val => {
        this.getAndSetTripFilterModel(val as DataFilterCreateEditForm);
      });
  }

  ngOnDestroy(): void {
    if (!this.tripsGridComponent.gridApi?.isDestroyed()) {
      this.tripsGridComponent.gridApi?.setFilterModel({});
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.tripsGridComponent = undefined as any;
  }

  onActiveFilterChanged(dataFilterId: number): void {
    this.activeFilterId = dataFilterId;
    this.tripsGridComponent.formGroup.controls.Period?.markAsDirty();
    const selectedFilterData = this.availableFilters$
      .getValue()
      .find(dataFilter => dataFilter.id === dataFilterId) as
      | TripsFilter
      | undefined;

    if (!selectedFilterData) return;

    this.selectedFilterName = selectedFilterData.Name;

    this.setGridPanelFormValue(selectedFilterData);
  }

  onClearGridFiltersClicked(): void {
    const noActiveFilters = this.availableFilters$
      .getValue()
      .map(dataFilter => ({ ...dataFilter, isActive: false }));
    this.availableFilters$.next(noActiveFilters);
    this.selectedFilterName = undefined;
  }

  addNewFilter = (): void => {
    if (this.checkForInvalidInterval()) return;
    const data = {
      addDataFilterPayload: this.constructDataFilterPayloadForSave(
        this.tripsGridComponent.formGroup.value as DataFilterCreateEditForm,
        false
      ),
    };

    if (
      this.tripsGridComponent.formGroup.controls.Period?.value?.selectedFilter
        ?.filterKey === DvFilterRangeKeys.Custom
    ) {
      this.tripDataFilterDialogService.openCustomPeriodForbiddenModal();
      return;
    }

    if (
      DvObjectUtils.areObjectValuesEmpty(data.addDataFilterPayload, [
        'Period',
      ]) &&
      !this.tripsGridComponent.gridOptions.context.extraFilter &&
      (
        this.tripsGridComponent.formGroup.controls.Period
          ?.value as DvPeriodPickerControlValue
      ).selectedFilter?.filterKey === DvFilterRangeKeys.All
    ) {
      this.showNoEmptyUpsertBanner();
      return;
    }
    this.tripDataFilterDialogService
      .openFilterCrudDialog(0, DataFilterAction.Add, data)
      .closed.pipe(take(1))
      .subscribe(event => {
        if (event.source !== 'confirm') return;
        this.fetchDataFilters(Number(event.responseData)).subscribe();
      });
  };

  onFilterAreaToggled(isOpen: boolean): void {
    localStorage.setItem(
      LocalStorageItems.TripFilterAreaToggleKey,
      String(isOpen)
    );
    this.isTripFilterAreaOpen = isOpen;
  }

  private getFilterMenuItems(isEditableFilter: boolean): VtFilterMenuItem[] {
    return [
      {
        title: this.translate.instant('Report.Filter.Update'),
        disabled: !isEditableFilter,
        icon: 'save',
        itemType: VtFilterMenuItemType.Update,
        clickFn: (dataFilterId: number) => {
          if (this.checkForInvalidInterval()) return;
          if (
            this.tripsGridComponent.formGroup.controls.Period?.value
              ?.selectedFilter?.filterKey === DvFilterRangeKeys.Custom
          ) {
            this.tripDataFilterDialogService.openCustomPeriodForbiddenModal();
          } else {
            this.updateDataFilter(dataFilterId);
          }
        },
      },
      {
        title: this.translate.instant('Report.Filter.Copy'),
        icon: 'copy',
        itemType: VtFilterMenuItemType.Copy,
        clickFn: (dataFilterId: number) => {
          this.tripDataFilterDialogService
            .openFilterCrudDialog(dataFilterId, DataFilterAction.Copy)
            .closed.pipe(
              switchMap(event => {
                if (event.source !== 'confirm') return of();

                return this.fetchDataFilters();
              }),
              take(1)
            )
            .subscribe();
        },
      },
      {
        title: this.translate.instant('Report.Filter.Delete'),
        itemType: VtFilterMenuItemType.Delete,
        icon: 'delete',
        disabled: !isEditableFilter,
        clickFn: (dataFilterId: number) => {
          this.api
            .delete(Entity.DataFilter, dataFilterId)
            .pipe(
              switchMap(() => this.fetchDataFilters()),
              take(1)
            )
            .subscribe();
        },
      },
      {
        title: this.translate.instant('ReportFilter.Edit'),
        icon: 'edit',
        itemType: VtFilterMenuItemType.Edit,
        disabled: !isEditableFilter,
        clickFn: (dataFilterId: number) => {
          const selectedFilterId = this.availableFilters$
            .getValue()
            .find(dataFilter => dataFilter.id === dataFilterId)!.id;
          this.dialog
            .open<EditFilterDialogData, EditTripFilterDialogComponent>({
              size: 'full',
              contentComponent: EditTripFilterDialogComponent,
              title: 'ReportFilter.Edit',
              confirmText: 'General.Confirm',
              cancelText: 'General.Cancel',
              hideFooter: true,
              data: {
                entityData: {
                  DataFilterID: selectedFilterId,
                },
              },
            })
            .closed.pipe(
              tap(({ source }) => {
                if (source !== 'confirm') return of();

                return this.fetchDataFilters().subscribe(() => {
                  this.onActiveFilterChanged(selectedFilterId);
                });
              }),
              take(1)
            )
            .subscribe();
        },
      },
    ];
  }

  private updateDataFilter(dataFilterId: number): void {
    const upsertPayload = this.constructDataFilterPayloadForSave(
      this.tripsGridComponent.formGroup.value as DataFilterCreateEditForm
    );

    if (
      DvObjectUtils.areObjectValuesEmpty(upsertPayload, ['Period']) &&
      !this.tripsGridComponent.gridOptions.context.extraFilter &&
      (
        this.tripsGridComponent.formGroup.controls.Period
          ?.value as DvPeriodPickerControlValue
      ).selectedFilter?.filterKey === DvFilterRangeKeys.All
    ) {
      this.showNoEmptyUpsertBanner();
      return;
    }

    this.api
      .upsert(Entity.DataFilter, String(dataFilterId), upsertPayload)
      .pipe(
        switchMap(_ => {
          return this.fetchDataFilters();
        }),
        take(1)
      )
      .subscribe();
  }

  private fetchDataFilters(newFilterId?: number): Observable<boolean> {
    if (newFilterId) {
      this.activeFilterId = newFilterId;
    }
    return this.api
      .getData({
        entity: Entity.DataFilter,
        fields: [
          ...DvDynamicFormUtils.getKeysForRequest([
            ...this.dataFilterElementsService.departmentAndDriverElements(),
            ...this.dataFilterElementsService.getPeriodElements(),
            ...this.dataFilterElementsService.getIntervalElements(),
          ]),
          'DataFilterID',
          'Name',
          'Editable',
          'FilterModel',
          'CustomFilter',
          'VisibleToAllUsers',
          'TranslationKey',
        ].filter(field => !excludedRequestValues.includes(field)),
      })
      .pipe(
        map(resp =>
          resp.data.map(
            (dataFilter: TripFilterDto) =>
              ({
                ...dataFilter,
                id: dataFilter.DataFilterID,
                name: dataFilter.TranslationKey
                  ? this.translate.instant(dataFilter.TranslationKey)
                  : dataFilter.Name || dataFilter.Description || '',
                isLocked: !dataFilter.Editable,
                filterMenuItems: this.getFilterMenuItems(!!dataFilter.Editable),
                type: FilterAreaUtils.getFilterType(
                  !!dataFilter.Editable,
                  !!dataFilter.VisibleToAllUsers
                ),
                isActive: dataFilter.DataFilterID === this.activeFilterId,
              }) as DvFilter
          )
        ),
        take(1),
        tap(filters => this.availableFilters$.next(filters)),
        map(() => true)
      );
  }

  private constructDataFilterPayloadForSave(
    formValue: DataFilterCreateEditForm,
    getOnlyDirtyValues = true,
    includeFilterModel = true
  ): TripFilterDto {
    const filterModel = this.tripsGridComponent.gridApi!.getFilterModel();

    // StartTime and EndTime filters can't be saved to datafilters
    delete filterModel['StartTime'];
    delete filterModel['EndTime'];

    const payload = this.filterDataSaveService.prepareDataForSave(
      (getOnlyDirtyValues
        ? DvFormUtils.getDirtyValues(this.tripsGridComponent.formGroup)
        : this.tripsGridComponent.formGroup.value) as DataFilterCreateEditForm,
      this.tripsGridComponent.departmentDriverAndPeriodElements,
      filterModel,
      TripDataFilterFormUtils.constructWeekDayArray(formValue),
      true
    );

    if (!includeFilterModel) {
      delete payload.FilterModel;
    }

    return payload;
  }

  private setGridPanelFormValue(data: TripFilterDto): void {
    DvDynamicFormUtils.setFirstData(
      this.tripsGridComponent.allElements,
      this.tripsGridComponent.formGroup,
      data,
      undefined,
      false
    );

    data.WeekDay?.forEach(day => {
      this.tripsGridComponent.formGroup.controls[
        String(day) as keyof WeekdaysForm
      ].setValue(true, { emitEvent: false });
    });

    if (!data.Interval) {
      this.tripsGridComponent.formGroup.controls.Interval?.setValue(
        FilterInterval.None,
        {
          emitEvent: false,
        }
      );
    }

    this.tripsGridComponent.intervalElements =
      this.dataFilterElementsService.toggleIntervalFieldsVisibility(
        this.tripsGridComponent.intervalElements,
        this.tripsGridComponent.formGroup
      );

    this.tripsGridComponent.formGroup.controls.Period?.setValue(
      this.getPeriodValue(data.AgGridFilter || {}, data.Period)
    );

    TripDataFilterFormUtils.setMultiselectDropdownValues(
      data.Driver || [],
      data.Department || [],
      this.tripsGridComponent.formGroup,
      this.tripsGridComponent.departmentDriverAndPeriodElements
    );
    this.tripsGridComponent.gridOptions.context.customFilter =
      data.CustomFilter;

    this.tripsGridComponent.gridOptions.context.extraFilter = {};
    this.setCustomFiltersToTripsGrid(data.CustomFilter, data.AgGridFilter);
  }

  private setCustomFiltersToTripsGrid(
    customFilter?: FilterModelWithKeys,
    filters?: FilterModelWithKeys
  ): void {
    let tripsGridFilterModel = filters;
    this.tripsGridComponent.gridOptions.context.customFilter =
      customFilter || {};

    if (tripsGridFilterModel) {
      // removes grid api warning about setting state for unknown column
      delete tripsGridFilterModel['CustomFilter'];
    }

    const tripsFilterAreaPeriodValue = this.tripsGridComponent.formGroup
      .controls.Period?.value as DvPeriodPickerControlValue;
    if (
      tripsFilterAreaPeriodValue.selectedFilter?.filterKey ===
        DvFilterRangeKeys.Custom &&
      tripsFilterAreaPeriodValue.start &&
      tripsFilterAreaPeriodValue.end
    ) {
      tripsGridFilterModel = {
        ...tripsGridFilterModel,
        ...CustomGridFilterUtils.getStartTimeCustomPeriodFilters(
          tripsFilterAreaPeriodValue.start,
          tripsFilterAreaPeriodValue.end,
          true
        ),
      };
    }
    this.tripsGridComponent.gridApi?.setFilterModel(tripsGridFilterModel!);
    this.tripsGridComponent.gridApi?.onFilterChanged();
  }

  private getAndSetTripFilterModel(formValue: DataFilterCreateEditForm): void {
    if (this.tripsGridComponent.formGroup.controls.Period?.dirty) {
      this.tripsGridComponent.gridOptions.context.extraFilter = undefined;
    }
    if (this.tripsGridComponent.formGroup.dirty) {
      const payload = this.constructDataFilterPayloadForSave(
        formValue as DataFilterCreateEditForm,
        false,
        true
      );

      this.api
        .postCustom<FilterModelWithKeys>('trip/getTripsFilterModel', payload)
        .subscribe(data => {
          this.setCustomFiltersToTripsGrid(
            (data as TripFilterDto).CustomFilter,
            data
          );
        });
    }
  }

  private showNoEmptyUpsertBanner(): void {
    this.snackBar.open(
      this.translate.instant('Trips.Filters.CanNotSaveEmpty'),
      SnackBarConfig.Warning
    );
  }

  private toggleFilterAreaVisibility(): void {
    const filterAreaStateValue = localStorage.getItem(
      LocalStorageItems.TripFilterAreaToggleKey
    );
    this.isTripFilterAreaOpen =
      filterAreaStateValue === 'true' || filterAreaStateValue === null;
  }

  private checkForInvalidInterval(): boolean {
    const { controls } = this.tripsGridComponent.formGroup;
    if (
      (!controls.IntervalFrom?.valid && !controls.IntervalFrom?.disabled) ||
      (!controls.IntervalTo?.valid && !controls.IntervalTo?.disabled)
    ) {
      this.tripDataFilterDialogService.openIntervalInvalidModal();
      return true;
    }
    return false;
  }

  private getPeriodValue(
    aggridFilters: FilterModelWithKeys,
    period?: PeriodInterval
  ): DvPeriodPickerControlValue {
    const periodControlValue: DvPeriodPickerControlValue = {
      selectedFilter: {
        title: period
          ? this.translate.instant(DvFilterRangeKeys[period])
          : String(this.translate.instant(DvFilterRangeKeys.All)).toUpperCase(),
        filterKey: period ? DvFilterRangeKeys[period] : DvFilterRangeKeys.All,
      },
    };

    if (period === PeriodInterval.Custom) {
      periodControlValue.start = new Date(
        (aggridFilters['StartTime'].conditions![0] as { dateFrom: Date })
          .dateFrom
      );
      periodControlValue.end = new Date(
        (aggridFilters['StartTime'].conditions![1] as { dateFrom: Date })
          .dateFrom
      );
    }

    return periodControlValue;
  }
}
